import React, { useState } from "react";
import DashboardLayout from "../../layouts/Dashboard";
import { dashboardCRUD } from "../../http";
import { yearEnd, yearStart } from "../../functions/dateHelpers";
import FilterDatePicker from "../../components/DatePicker/FilterDatePiecker";
import { Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { sum } from "../../functions/linqSimulation";
import { roundTo2 } from "../../functions/formatters";
import XLSX from "xlsx-js-style";
import { Button } from "@mui/material";

const indices = [
    "ГРИПП", "ОРВИ", "Энтеровирус", "Корь IgM", "Корь IgG",
    "Краснуха IgM", "Краснуха IgG", "COVID-19", "Ротавирус от людей", "Полиомиелит"
];
const subColumns = ["план", "факт", "%", "не соответствие"];

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const excelCell = (value) => ({
    v: value === null || value === undefined ? "" : value,
    t: typeof value === "number" ? "n" : "s",
});

const exportReport = (data, dateRange) => {
    if (!data.length) return;

    const headers = ["№", "Договора с ДСЭК (Регионы)"];
    indices.forEach((name) => headers.push(name, "", "", ""));
    const subHeaders = ["", "", ...indices.flatMap(() => subColumns)];
    const rows = data.map((item, index) => [
        index + 1,
        item.name || "",
        ...indices.flatMap((_, metricIndex) => [
            0,
            roundTo2(toNumber(item[`quantity${metricIndex + 1}`])),
            0,
            roundTo2(toNumber(item[`inconsistenceQuantity${metricIndex + 1}`])),
        ]),
    ]);
    const totals = ["", "Всего:", ...indices.flatMap((_, metricIndex) => [
        0,
        roundTo2(sum(data, (item) => toNumber(item[`quantity${metricIndex + 1}`]))),
        0,
        roundTo2(sum(data, (item) => toNumber(item[`inconsistenceQuantity${metricIndex + 1}`]))),
    ])];
    // Явные типы ячеек и конечные числовые значения сохраняют данные в Excel 2016.
    const sheet = XLSX.utils.aoa_to_sheet(
        [headers, subHeaders, ...rows, totals].map((row) => row.map(excelCell))
    );
    sheet["!merges"] = indices.map((_, index) => ({
        s: { r: 0, c: 2 + index * 4 },
        e: { r: 0, c: 5 + index * 4 },
    }));
    sheet["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });
    sheet["!merges"].push({ s: { r: 0, c: 1 }, e: { r: 1, c: 1 } });
    sheet["!cols"] = [{ wch: 6 }, { wch: 34 }, ...indices.flatMap(() => [10, 10, 8, 18])];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Отчет");
    const start = String(dateRange.operand1 || "").slice(0, 10);
    const end = String(dateRange.operand2 || "").slice(0, 10);
    XLSX.writeFile(workbook, `Корь_ОРВИ_Грипп_госзаказ_${start}_${end}.xlsx`);
};

const RequestTarificationsQuantityIndices = () => {
    const [query, setQuery] = useState({
        paging: { skip: 0 },
        filter: { researchEndDateTime: { operand1: yearStart(), operand2: yearEnd(), operator: "between" } }
    });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchAsync = async () => {
        setLoading(true);
        try {
            setData(await dashboardCRUD.post("requestTarifications/quantity/indices/table1", query));
        } finally {
            setLoading(false);
        }
    };

    const updateDate = (field, value) => {
        if (!value) return;
        setQuery({
            ...query,
            filter: {
                ...query.filter,
                researchEndDateTime: { ...query.filter.researchEndDateTime, [field]: value }
            }
        });
    };

    const renderValues = (item) => indices.map((_, index) => <React.Fragment key={index}>
        <td className="ant-table-cell">0</td>
        <td className="ant-table-cell">{roundTo2(item[`quantity${index + 1}`])}</td>
        <td className="ant-table-cell">0</td>
        <td className="ant-table-cell">{roundTo2(item[`inconsistenceQuantity${index + 1}`])}</td>
    </React.Fragment>);

    return <DashboardLayout>
        <Stack direction="row" alignItems="center" justifyContent="normal">
            <label style={{ minWidth: "360px" }}>Планирование и исполнение исследований в рамках госзаказа за период</label>
            <FilterDatePicker label="от" value={query.filter.researchEndDateTime.operand1}
                handleFunction={(value) => updateDate("operand1", value)} style={{ width: "250px" }} />
            <FilterDatePicker label="до" value={query.filter.researchEndDateTime.operand2}
                handleFunction={(value) => updateDate("operand2", value)} style={{ width: "250px", marginLeft: "10px" }} />
            <LoadingButton loading={loading} style={{ marginLeft: "10px" }} onClick={fetchAsync}>Сформировать</LoadingButton>
            <Button variant="outlined" style={{ marginLeft: "10px" }} onClick={() => exportReport(data, query.filter.researchEndDateTime)} disabled={!data.length}>
                Скачать Excel
            </Button>
        </Stack>

        <div style={{ height: "10px" }}></div>
        <table style={{ width: "100%" }}>
            <thead className="ant-table-thead">
                <tr>
                    <th rowSpan={2} className="ant-table-cell"></th>
                    <th rowSpan={2} style={{ verticalAlign: "top" }} className="ant-table-cell">Договора с ДСЭК (Регионы)</th>
                    {indices.map((name) => <th key={name} colSpan={4} className="ant-table-cell">{name}</th>)}
                </tr>
                <tr>{indices.flatMap((_, index) => subColumns.map((name) => <th key={`${index}-${name}`}>{name}</th>))}</tr>
            </thead>
            <tbody className="ant-table-tbody">
                {data.map((item, index) => <tr className="ant-table-row" key={item.name || index}>
                    <td className="ant-table-cell">{index + 1}</td>
                    <td className="ant-table-cell">{item.name}</td>
                    {renderValues(item)}
                </tr>)}
                {data.length > 0 && <tr className="ant-table-row" style={{ backgroundColor: "#3cb37188" }}>
                    <td></td><td>Всего:</td>
                    {indices.map((_, index) => <React.Fragment key={index}>
                        <td className="ant-table-cell">0</td>
                        <td className="ant-table-cell">{roundTo2(sum(data, item => item[`quantity${index + 1}`]))}</td>
                        <td className="ant-table-cell">0</td>
                        <td className="ant-table-cell">{roundTo2(sum(data, item => item[`inconsistenceQuantity${index + 1}`]))}</td>
                    </React.Fragment>)}
                </tr>}
            </tbody>
        </table>
    </DashboardLayout>;
};

export default RequestTarificationsQuantityIndices;
